import { MessageFilled } from "@ant-design/icons";
import { Flex } from "antd";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Point,
    SubtaskFinGroupPhotos,
    TaskAssortmentMatrix,
    TaskStatus,
} from "tm-shared/types";

import { GalleryUploader } from "@/domains/media";
import { usePointDistance } from "@/domains/point";
import {
    CommentModal,
    StatusCycleButton,
    TaskInfoModal,
    useActiveScrollNavigator,
    useCheckCommentOnReject,
    useStickyFilteredItems,
    useSubtaskConfigSelector,
    useSubtaskFinGroupPhotosSelector,
} from "@/domains/tasks";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { BottomSheet, OutlineIconButton, PrimaryText } from "@/shared/ui";
import {
    mutateAssortmentFinGroupPhotosEntityPhotos,
    patchAssortmentFinGroupPhotosEntity,
} from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";
import { buildPhotoCaptions } from "@/utils/photo/captions";

import { useAssortmentMatrixCompletion } from "../hooks/useAssortmentMatrixCompletion";
import { StepButton } from "../StepButton";
import { StyledList } from "../Styled";
import { CameraPreviewOverlay } from "./components/CameraPreviewOverlay";
import { TaskFinGroupPhotos } from "./components/TaskFinGroupPhotos";
import { StyledBottomSheetTop, StyledMainContainer } from "./Styled";

interface FinGroupPhotosProps {
    subtask: SubtaskFinGroupPhotos;
    point: Point;
    task: TaskAssortmentMatrix;
}

export const FinGroupPhotos: FC<FinGroupPhotosProps> = ({
    subtask,
    point,
    task,
}) => {
    const dispatch = useAppDispatch();
    const [modal, setModal] = useState<"comment" | "taskInfo" | null>(null);

    const pointId = point.id;
    const taskId = task.id;
    const contractId = subtask.contractId;
    const subtaskId = subtask.subtaskId;

    const { firstName, lastName } = useAppSelector((s) => s.user);

    const subtaskPayload = useSubtaskFinGroupPhotosSelector({
        taskId,
        subtaskId,
    });

    const { activeItem, showUncompleted } = useSubtaskConfigSelector({
        pointId,
        taskId,
        subtaskId,
    });

    const photoCategories = useMemo(
        () => Array.from(new Set(subtask.context.photos)),
        [subtask.context.photos],
    );

    const activePayload = useMemo(
        () =>
            activeItem
                ? {
                      id: activeItem,
                      payload: subtaskPayload?.tasks?.[activeItem] ?? null,
                      status:
                          subtaskPayload?.tasks?.[activeItem]?.status ??
                          TaskStatus.Incomplete,
                  }
                : null,
        [activeItem, subtaskPayload],
    );

    const prevRefId = useRef<string | null>(null);

    const resetStatusIfNeeded = useCallback(
        (id: string) => {
            const payload = subtaskPayload?.tasks?.[id];

            if (!payload) return;

            if (payload.status === TaskStatus.Rejected && !payload.comment) {
                dispatch(
                    patchAssortmentFinGroupPhotosEntity({
                        taskId,
                        contractId,
                        id,
                        subtaskId,
                        patch: { status: TaskStatus.Incomplete },
                    }),
                );
            }
        },
        [subtaskPayload?.tasks, dispatch, taskId, contractId, subtaskId],
    );

    useEffect(() => {
        const prev = prevRefId.current;

        if (prev && activeItem !== prev) {
            resetStatusIfNeeded(prev);
        }

        prevRefId.current = activeItem ?? null;
    }, [activeItem, resetStatusIfNeeded]);

    const onChangeTaskStatus = useCallback(
        (value: TaskStatus, id?: string) => {
            const targetId = id ?? activePayload?.id;

            if (!targetId) return;

            dispatch(
                patchAssortmentFinGroupPhotosEntity({
                    taskId,
                    contractId,
                    subtaskId,
                    id: targetId,
                    patch: { status: value, comment: "" },
                }),
            );
        },
        [activePayload, dispatch, taskId, contractId, subtaskId],
    );

    const handlerMessage = useCallback(() => {
        if (!activePayload || activePayload.status === TaskStatus.Incomplete)
            return;

        setModal("comment");
    }, [activePayload]);

    const handleChangeComment = useCallback(
        (newComment: string) => {
            if (!activePayload) return;

            dispatch(
                patchAssortmentFinGroupPhotosEntity({
                    taskId,
                    contractId,
                    id: activePayload.id,
                    subtaskId,
                    patch: { comment: newComment },
                }),
            );
        },
        [activePayload, contractId, dispatch, subtaskId, taskId],
    );

    const onClickInfoButton = () => {
        setModal("taskInfo");
    };

    const onChangeActive = useCallback(
        (id: string) => {
            dispatch(
                setMdTasksConfig({
                    pointId,
                    taskId,
                    subtaskId,
                    field: "activeItem",
                    value: id,
                }),
            );
        },
        [dispatch, pointId, taskId, subtaskId],
    );

    const { filteredItems: filteredTasks } = useStickyFilteredItems({
        items: photoCategories,
        showUncompleted: showUncompleted ?? false,
        getItemId: (category) => category,
        payloadsMap: subtaskPayload?.tasks ?? {},
        isIncomplete: (payload) => {
            return !payload || payload.status === TaskStatus.Incomplete;
        },
    });

    const { validateAndNotify } = useCheckCommentOnReject();

    const handleCheckCommentOnReject = useCallback(
        (category: string | null) => {
            const previousCategory = prevRefId.current;

            if (previousCategory) {
                const categoryPayload =
                    subtaskPayload?.tasks?.[previousCategory];

                if (validateAndNotify(categoryPayload)) {
                    return;
                }
            }

            if (category) onChangeActive(category);
        },
        [onChangeActive, subtaskPayload?.tasks, validateAndNotify],
    );

    const { activeIndex, getItemRef, onNext, onPrev, scrollToActive } =
        useActiveScrollNavigator({
            ids: filteredTasks,
            activeId: activeItem ?? null,
            onChangeActive: handleCheckCommentOnReject,
        });

    const onCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                pointId,
                taskId,
                subtaskId,
                field: "activeItem",
                value: null,
            }),
        );
    }, [dispatch, pointId, taskId, subtaskId]);

    const onDeletePhoto = useCallback(
        (photoId: string) => {
            if (!activePayload) return;

            dispatch(
                mutateAssortmentFinGroupPhotosEntityPhotos({
                    taskId,
                    contractId,
                    id: activePayload.id,
                    option: "remove",
                    photoId,
                    subtaskId,
                }),
            );
        },
        [activePayload, dispatch, taskId, contractId, subtaskId],
    );

    const onUploadPhoto = useCallback(
        (photo: string) => {
            if (!activePayload) return;

            dispatch(
                mutateAssortmentFinGroupPhotosEntityPhotos({
                    taskId,
                    contractId,
                    id: activePayload.id,
                    option: "add",
                    photo,
                    subtaskId,
                }),
            );
        },
        [activePayload, dispatch, taskId, contractId, subtaskId],
    );

    useAssortmentMatrixCompletion({
        taskId,
        subtaskId,
        expectedCount: photoCategories?.length ?? 0,
        payloadsMap: subtaskPayload?.tasks ?? null,
    });

    const { distance } = usePointDistance({
        pointGeo: point.market.building.geo,
    });

    const captions = useMemo(
        () =>
            buildPhotoCaptions({
                pointName: point.name,
                taskName: task.name,
                userFullName: `${firstName} ${lastName}`,
                distance,
            }),
        [distance, firstName, lastName, point.name, task.name],
    );

    useEffect(() => {
        scrollToActive();
    }, [subtaskPayload?.completed, scrollToActive]);

    return (
        <>
            <StyledMainContainer>
                <StyledList>
                    {filteredTasks.map((category) => {
                        const isActive = activeItem === category;
                        const currentPayload =
                            subtaskPayload?.tasks?.[category];

                        return (
                            <div key={category} ref={getItemRef(category)}>
                                <TaskFinGroupPhotos
                                    item={category}
                                    isActive={isActive}
                                    onChangeActive={onChangeActive}
                                    payload={currentPayload}
                                    onChangeTaskStatus={onChangeTaskStatus}
                                />
                            </div>
                        );
                    })}
                </StyledList>

                <StepButton
                    task={task}
                    pointId={pointId}
                    currentCompleted={subtaskPayload?.completed}
                />
            </StyledMainContainer>

            <BottomSheet
                open={!!activeItem}
                onClose={onCloseBottomSheet}
                onNext={onNext}
                onPrev={onPrev}
                onClickInfoButton={onClickInfoButton}
            >
                <StyledBottomSheetTop>
                    <PrimaryText>{photoCategories[activeIndex]}</PrimaryText>
                    <Flex gap={20} align="center">
                        <OutlineIconButton
                            icon={<MessageFilled />}
                            onClick={handlerMessage}
                            disabled={
                                activePayload?.status === TaskStatus.Incomplete
                            }
                        />
                        <StatusCycleButton
                            value={
                                activePayload?.status ?? TaskStatus.Incomplete
                            }
                            onChange={onChangeTaskStatus}
                            states={[
                                TaskStatus.Incomplete,
                                TaskStatus.Rejected,
                            ]}
                            disabled={
                                activePayload?.status === TaskStatus.Completed
                            }
                        />
                    </Flex>
                </StyledBottomSheetTop>

                <GalleryUploader
                    previewTitle={filteredTasks[activeIndex]}
                    onDelete={onDeletePhoto}
                    onUpload={onUploadPhoto}
                    activeItemId={activePayload?.id}
                    pointId={pointId}
                    savedPhotos={activePayload?.payload?.photos ?? []}
                    disabled={activePayload?.status === TaskStatus.Rejected}
                    captionLines={captions}
                    cameraProps={{
                        previewOverlay: activePayload?.id ? (
                            <CameraPreviewOverlay
                                fingroup={activePayload?.id}
                            />
                        ) : undefined,
                    }}
                />
            </BottomSheet>

            {modal === "comment" && activePayload && (
                <CommentModal
                    onCloseModal={() => setModal(null)}
                    onConfirm={handleChangeComment}
                    initialComment={activePayload.payload?.comment ?? ""}
                    taskStatus={activePayload.status}
                />
            )}

            {modal === "taskInfo" && (
                <TaskInfoModal
                    onCloseModal={() => setModal(null)}
                    title={"Фото фінгруп"}
                    description={
                        "Зробіть фото для кожної фінгрупи окремо. На фото має бути видно викладку товарів цієї групи на полиці. Якщо вся фінгрупа не поміщається в один кадр, зробіть кілька фото. Якщо фото зробити неможливо — виберіть статус і додайте коментар."
                    }
                />
            )}
        </>
    );
};
